/* Staffing — firm-wide capacity chart + per-person grid.
 *
 * Layout:
 *   ┌─────────────────────────────────────────────────────┐
 *   │  ← 4w   Today   4w →                                │
 *   │  Stacked capacity bars (billable orange, etc.)      │
 *   ├─────────────────────────────────────────────────────┤
 *   │  Person · CW18 · CW19 · CW20 · …                    │
 *   │  ▸ Alice         50%   75%   100% …                 │
 *   │      └ Project A 50%   50%    50% …  + Add project  │
 *   │      └ Vacation   0%   25%     0%                   │
 *   │  ▸ Bob           …                                  │
 *   └─────────────────────────────────────────────────────┘
 *
 * Click a row to expand its per-project breakdown. Click any cell
 * to open the edit popup. + Add project appears below the last
 * project row when expanded.
 *
 * Conflict warning: upsertStaffingEntry throws err.code === 'conflict'
 * when the row moved since the popup opened — the dialog catches
 * that and prompts the user to discard or overwrite.
 */
import React from 'react';
import {
  fetchStaffingCapacity, listStaffingEntries,
  upsertStaffingEntry, deleteStaffingEntry, listPeople, listProjects, listTaxonomies,
  fetchBookingRollup, listStaffingCategories,
} from '../api.js';
import { EntityLink, entityHref, entityHandle, fmtPct } from './primitives.jsx';
import { useAnchoredPosition, HIDDEN_ANCHORED_STYLE } from '../lib/use-anchored-position.jsx';

/* ── ISO-week helpers ─────────────────────────────────────────── */
function isoWeekOf(date) {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const isoYear = d.getUTCFullYear();
  const jan4 = new Date(Date.UTC(isoYear, 0, 4));
  const week = 1 + Math.round(((d.getTime() - jan4.getTime()) / 86400000 - 3 + ((jan4.getUTCDay() || 7) - 1)) / 7);
  return { isoWeek: `${isoYear}-W${String(week).padStart(2, '0')}`, year: isoYear, week };
}

function buildWindow(centerDate, before, after) {
  const out = [];
  for (let i = -before; i <= after; i++) {
    const d = new Date(centerDate);
    d.setUTCDate(d.getUTCDate() + i * 7);
    out.push(isoWeekOf(d));
  }
  return out;
}

const WEEKS_BEFORE = 4;
const WEEKS_AFTER  = 11;
const WEEKS_BEFORE_MOBILE = 1;
const WEEKS_AFTER_MOBILE  = 6;

/* Viewport-aware sizing. The grid + capacity chart cram a 16-week
 * window across the screen on desktop; on phones we narrow the
 * window AND shrink the person column so the cells stay tappable
 * (>=42px) inside the parent's horizontal scroll. */
function useIsMobile(threshold = 768) {
  const [mobile, setMobile] = React.useState(() => typeof window !== 'undefined' && window.matchMedia(`(max-width: ${threshold}px)`).matches);
  React.useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${threshold}px)`);
    const onChange = () => setMobile(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [threshold]);
  return mobile;
}

/* Categories are admin-managed (see Admin → Categories) and shared with
 * the booking grid. This fallback mirrors the seed so the grid still
 * renders if the fetch hasn't resolved yet (or the endpoint 404s on a DB
 * that hasn't had migration 0121 applied). 'client-project' is the
 * project dimension, prepended separately. */
const FALLBACK_CATEGORIES = [
  { slug: 'sales',          label: 'Sales',          color: '#2E7DAF', clientLinked: true  },
  { slug: 'internal',       label: 'Internal',       color: '#715AAE', clientLinked: false },
  { slug: 'recruiting',     label: 'Recruiting',     color: '#C98A2B', clientLinked: false },
  { slug: 'vacation',       label: 'Vacation',       color: '#3CA88A', clientLinked: false },
  { slug: 'public-holiday', label: 'Public holiday', color: '#A0A0A0', clientLinked: false },
  { slug: 'sick',           label: 'Sick',           color: '#C25450', clientLinked: false },
];

/* Type dropdown options for the staffing plan: the project dimension
 * first, then every active category. */
const typeOptions = (categories) =>
  [{ value: 'client-project', label: 'Client Project' },
   ...(categories || []).map(c => ({ value: c.slug, label: c.label }))];

export const StaffingView = () => {
  const isMobile = useIsMobile();
  const [centerDate, setCenterDate] = React.useState(() => new Date());
  const weeks = React.useMemo(
    () => buildWindow(centerDate,
      isMobile ? WEEKS_BEFORE_MOBILE : WEEKS_BEFORE,
      isMobile ? WEEKS_AFTER_MOBILE  : WEEKS_AFTER),
    [centerDate, isMobile],
  );
  const today = React.useMemo(() => isoWeekOf(new Date()).isoWeek, []);

  const fromWeek = weeks[0].isoWeek;
  const toWeek   = weeks[weeks.length - 1].isoWeek;

  const [capacity, setCapacity] = React.useState(null);
  const [entries,  setEntries]  = React.useState([]);
  const [rollup,   setRollup]   = React.useState([]);     // weekly booking actuals
  const [people,   setPeople]   = React.useState([]);
  const [projects, setProjects] = React.useState([]);
  const [clients,  setClients]  = React.useState([]);
  const [categories, setCategories] = React.useState(FALLBACK_CATEGORIES);
  const [error,    setError]    = React.useState(null);
  const [editing,  setEditing]  = React.useState(null);   // { userId, isoWeek }
  const [expanded, setExpanded] = React.useState(() => new Set());
  // Default to the mix of actuals + plan; booked cells render locked.
  const [viewMode, setViewMode] = React.useState('mixed'); // 'mixed' | 'plan'

  // Search + filters across profile data.
  const [search,         setSearch]         = React.useState('');
  const [filterCompany,  setFilterCompany]  = React.useState('');
  const [filterCountry,  setFilterCountry]  = React.useState('');
  const [filterFunction, setFilterFunction] = React.useState('');
  const [filterProject,  setFilterProject]  = React.useState('');  // project id
  const [filtersOpen,    setFiltersOpen]    = React.useState(false);
  const filterAnchorRef = React.useRef(null);

  // Synchronised horizontal scroll: chart (top) + table (bottom)
  // move together when either is scrolled, and when the week-shift
  // buttons fire.
  const chartScrollRef = React.useRef(null);
  const tableScrollRef = React.useRef(null);
  const syncingRef     = React.useRef(false);
  const mirrorScroll = (srcRef, dstRef) => () => {
    if (syncingRef.current) return;
    const src = srcRef.current, dst = dstRef.current;
    if (!src || !dst) return;
    syncingRef.current = true;
    dst.scrollLeft = src.scrollLeft;
    requestAnimationFrame(() => { syncingRef.current = false; });
  };

  React.useEffect(() => { window.addEventListener('keydown', onKey); return () => window.removeEventListener('keydown', onKey); });
  function onKey(e) {
    if (editing) return;
    if (e.key === 'ArrowLeft')  shiftWeeks(-1);
    if (e.key === 'ArrowRight') shiftWeeks(1);
  }

  const reload = React.useCallback(() => {
    setError(null);
    Promise.all([
      fetchStaffingCapacity({ from: fromWeek, to: toWeek, projectId: filterProject || undefined }).catch(() => null),
      listStaffingEntries({ from: fromWeek, to: toWeek }).catch(() => []),
      listPeople().catch(() => []),
      listProjects().catch(() => []),
      listTaxonomies().then(t => t.clients || []).catch(() => []),
      fetchBookingRollup({ from: fromWeek, to: toWeek }).catch(() => []),
      listStaffingCategories().catch(() => FALLBACK_CATEGORIES),
    ]).then(([cap, ent, ppl, prj, cli, rol, cats]) => {
      setCapacity(cap); setEntries(ent); setPeople(ppl); setProjects(prj); setClients(cli); setRollup(rol);
      setCategories(cats && cats.length ? cats : FALLBACK_CATEGORIES);
    }).catch(err => setError(err.message || String(err)));
  }, [fromWeek, toWeek, filterProject]);

  React.useEffect(reload, [reload]);

  // userId → isoWeek → list of entries
  const grid = React.useMemo(() => {
    const m = new Map();
    for (const e of entries) {
      const wkMap = m.get(e.userId) || new Map();
      const arr   = wkMap.get(e.isoWeek) || [];
      arr.push(e);
      wkMap.set(e.isoWeek, arr);
      m.set(e.userId, wkMap);
    }
    return m;
  }, [entries]);

  // userId → isoWeek → { totalPercent, items: Map(key → item) }. Booked
  // actuals rolled up to weekly percentages, lined up against the plan.
  const bookedByUser = React.useMemo(() => {
    const m = new Map();
    for (const w of rollup) {
      const um = m.get(w.userId) || new Map();
      const items = new Map();
      for (const it of (w.items || [])) items.set(it.key, it);
      um.set(w.isoWeek, { totalPercent: w.totalPercent, totalHours: w.totalHours, items });
      m.set(w.userId, um);
    }
    return m;
  }, [rollup]);
  const EMPTY_BOOKED = React.useMemo(() => new Map(), []);

  // Distinct filter options from the loaded people.
  const options = React.useMemo(() => {
    const companies = new Set(), countries = new Set(), functions = new Set();
    for (const p of people) {
      if (p.company) companies.add(p.company);
      if (p.country) countries.add(p.country);
      if (p.functionName) functions.add(p.functionName);
    }
    const sort = s => [...s].sort((a, b) => a.localeCompare(b));
    return { companies: sort(companies), countries: sort(countries), functions: sort(functions) };
  }, [people]);

  // Set of userIds with at least one entry on the selected project,
  // within the visible window. Drives "show only people on this
  // project" + force-expand behaviour.
  const projectUserIds = React.useMemo(() => {
    if (!filterProject) return null;
    const ids = new Set();
    for (const e of entries) {
      if (e.projectId === filterProject && e.percentage > 0) ids.add(e.userId);
    }
    return ids;
  }, [entries, filterProject]);

  // Apply search + filters. Search spans every profile field we hold.
  const filteredPeople = React.useMemo(() => {
    const tokens = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return people.filter(p => {
      if (filterCompany  && p.company      !== filterCompany)  return false;
      if (filterCountry  && p.country      !== filterCountry)  return false;
      if (filterFunction && p.functionName !== filterFunction) return false;
      if (projectUserIds && !projectUserIds.has(p.id))         return false;
      if (!tokens.length) return true;
      const hay = [p.name, p.title, p.company, p.country, p.functionName, p.email, p.role]
        .filter(Boolean).join(' ').toLowerCase();
      return tokens.every(t => hay.includes(t));
    });
  }, [people, search, filterCompany, filterCountry, filterFunction, projectUserIds]);

  const activeFilters = [
    filterCompany  && { key: 'company',  label: `Org: ${filterCompany}`,   clear: () => setFilterCompany('') },
    filterCountry  && { key: 'country',  label: `Country: ${filterCountry}`, clear: () => setFilterCountry('') },
    filterFunction && { key: 'function', label: `Role: ${filterFunction}`,  clear: () => setFilterFunction('') },
    filterProject  && { key: 'project',  label: `Project: ${projects.find(p => p.id === filterProject)?.name || filterProject}`, clear: () => setFilterProject('') },
  ].filter(Boolean);
  const clearAll = () => { setSearch(''); setFilterCompany(''); setFilterCountry(''); setFilterFunction(''); setFilterProject(''); };

  const shiftWeeks = n => {
    const d = new Date(centerDate);
    d.setUTCDate(d.getUTCDate() + n * 7);
    setCenterDate(d);
  };

  const toggleExpand = (id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const openEdit = (userId, isoWeek, existing) => {
    setEditing({ userId, isoWeek, existing: existing || null });
  };

  const closeEdit = (saved) => {
    setEditing(null);
    if (saved) reload();
  };

  // Drag-to-fill commit: write the dragged value to every week in the
  // range (overwriting whatever was there), then reload.
  const fillCells = async ({ userId, projectType, projectId, clientId, percentage, note, isoWeeks }) => {
    setError(null);
    try {
      for (const isoWeek of isoWeeks) {
        await upsertStaffingEntry({
          userId, isoWeek, projectType,
          projectId: projectType === 'client-project' ? projectId : null,
          clientId: clientId || null,
          percentage: Number(percentage), note: note || '',
        });
      }
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      reload();
    }
  };

  const step = isMobile ? 2 : 4;

  return (
    <div style={{padding: isMobile ? '14px 12px' : '24px 28px'}}>
      <div style={{
        display:'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent:'space-between',
        gap: isMobile ? 10 : 0,
      }}>
        <div>
          <h1 style={{fontSize: isMobile ? 18 : 22, fontWeight:600, margin:'0 0 4px'}}>Staffing</h1>
          <p style={{color:'var(--fg-muted)', fontSize: isMobile ? 11.5 : 13, marginTop:0, marginBottom:0}}>
            Firm-wide capacity, by ISO week. Window: {fromWeek} → {toWeek}.
          </p>
        </div>
        <div style={{display:'flex', gap:8, alignItems:'center', justifyContent: isMobile ? 'space-between' : 'flex-end'}}>
          <ViewModeToggle value={viewMode} onChange={setViewMode}/>
          <button onClick={() => shiftWeeks(-step)} style={btnStyle}>← {step}w</button>
          <button onClick={() => shiftWeeks(step)}  style={btnStyle}>{step}w →</button>
        </div>
      </div>

      {viewMode === 'mixed' && (
        <div style={{display:'flex', alignItems:'center', gap:6, marginTop:8, fontSize:11.5, color:'var(--fg-muted)'}}>
          <LockIcon/> Booked actuals (locked) · plain cells are planned and editable
        </div>
      )}

      {/* Search + one Filters button; active filters render as
          removable chips below. Same layout on desktop + mobile —
          the panel is a block dropdown so it works in both. */}
      <div style={{
        display:'flex', gap:8, marginTop:12, alignItems:'center',
        flexDirection: isMobile ? 'column' : 'row',
      }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search people — name, title, org, country, role…"
          style={{
            flex: isMobile ? 'none' : '1 1 240px', width: isMobile ? '100%' : undefined, minWidth:0,
            padding:'8px 10px', border:'1px solid var(--border)', borderRadius:6,
            fontSize:13, background:'var(--bg-card)', boxSizing:'border-box',
          }}
        />
        <div ref={filterAnchorRef} style={{position:'relative', width: isMobile ? '100%' : 'auto'}}>
          <button
            onClick={() => setFiltersOpen(o => !o)}
            style={{
              ...btnStyle, width: isMobile ? '100%' : 'auto',
              display:'inline-flex', alignItems:'center', justifyContent:'center', gap:6,
              borderColor: filtersOpen || activeFilters.length ? 'var(--orange)' : 'var(--border)',
            }}
          >
            Filters{activeFilters.length > 0 && (
              <span style={{
                background:'var(--orange)', color:'#fff', borderRadius:9,
                fontSize:10, fontWeight:700, padding:'1px 6px', lineHeight:1.4,
              }}>{activeFilters.length}</span>
            )}
          </button>
          {filtersOpen && (
            <FilterPanel
              isMobile={isMobile}
              anchorRef={filterAnchorRef}
              options={options}
              projects={projects}
              values={{ filterCompany, filterCountry, filterFunction, filterProject }}
              setters={{ setFilterCompany, setFilterCountry, setFilterFunction, setFilterProject }}
              onClose={() => setFiltersOpen(false)}
            />
          )}
        </div>
      </div>

      {/* Active filter chips. */}
      {activeFilters.length > 0 && (
        <div style={{display:'flex', flexWrap:'wrap', gap:6, marginTop:8, alignItems:'center'}}>
          {activeFilters.map(f => (
            <span key={f.key} style={{
              display:'inline-flex', alignItems:'center', gap:6,
              background:'rgba(255,100,40,0.10)', border:'1px solid var(--orange)',
              borderRadius:14, padding:'3px 6px 3px 10px', fontSize:11.5,
            }}>
              {f.label}
              <button onClick={f.clear} title="Remove" style={{
                all:'unset', cursor:'pointer', color:'var(--orange)', fontWeight:700,
                lineHeight:1, padding:'0 2px',
              }}>×</button>
            </span>
          ))}
          <button onClick={clearAll} style={{...btnStyle, padding:'4px 8px', fontSize:11.5}}>Clear all</button>
        </div>
      )}

      {error && <div style={errStyle}>{error}</div>}

      <CapacityChart
        weeks={weeks} capacity={capacity} categories={categories} today={today} isMobile={isMobile}
        scrollRef={chartScrollRef}
        onScroll={mirrorScroll(chartScrollRef, tableScrollRef)}
      />

      <div
        ref={tableScrollRef}
        onScroll={mirrorScroll(tableScrollRef, chartScrollRef)}
        style={{
          marginTop: isMobile ? 12 : 18,
          background:'var(--bg)', overflowX:'auto',
        }}>
        <div style={gridHeader(weeks.length, isMobile)}>
          <div style={{...STICKY_COL_HEAD, padding: isMobile ? '6px 10px' : '8px 12px'}}>Person</div>
          {weeks.map(w => (
            <div key={w.isoWeek} style={headerCell(w.isoWeek === today, isMobile)}>
              CW{w.week}
            </div>
          ))}
        </div>

        {filteredPeople.length === 0 && (
          <div style={{padding:24, textAlign:'center', color:'var(--fg-muted)', fontSize:12.5}}>
            {people.length === 0 ? 'No people loaded yet.' : 'No people match the current search / filters.'}
          </div>
        )}

        {filteredPeople.map(p => (
          <PersonRow
            key={p.id}
            person={p}
            weeks={weeks}
            isMobile={isMobile}
            entriesByWeek={grid.get(p.id) || new Map()}
            today={today}
            projects={projects}
            clients={clients}
            categories={categories}
            /* When a project filter is active, force every row open
               and restrict the breakdown to that one project line. */
            expanded={filterProject ? true : expanded.has(p.id)}
            onlyProjectId={filterProject || null}
            onToggle={() => toggleExpand(p.id)}
            onEdit={openEdit}
            onFill={fillCells}
            viewMode={viewMode}
            bookedByWeek={viewMode === 'mixed' ? (bookedByUser.get(p.id) || EMPTY_BOOKED) : null}
          />
        ))}
      </div>

      {editing && (
        <EditStaffingDialog
          {...editing}
          projects={projects}
          clients={clients}
          categories={categories}
          onClose={closeEdit}
        />
      )}
    </div>
  );
};

/* ── Filter panel ─────────────────────────────────────────────
 * One popover holding every filter category. Active selections
 * surface as removable chips in the parent. Same panel on desktop
 * + mobile; it anchors under the Filters button (full-width on
 * mobile) and closes on outside-click or Escape. */
const FilterPanel = ({ isMobile, anchorRef, options, projects, values, setters, onClose }) => {
  const ref = React.useRef(null);
  const pos = useAnchoredPosition(anchorRef, true, {
    align: 'start', matchWidth: isMobile, width: isMobile ? undefined : 260, maxHeight: 420,
  });
  React.useEffect(() => {
    const onDoc = e => { if (!ref.current?.contains(e.target)) onClose(); };
    const onEsc = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onEsc); };
  }, [onClose]);

  const sortedProjects = [...projects].sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  const Row = ({ label, value, onChange, children }) => (
    <div style={{marginBottom:12}}>
      <div style={{fontSize:10.5, fontWeight:600, color:'var(--fg-muted)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:4}}>{label}</div>
      <select value={value} onChange={e => onChange(e.target.value)} style={{
        width:'100%', padding:'8px 10px', border:'1px solid var(--border)', borderRadius:6,
        fontSize:13, background:'var(--bg-card)', cursor:'pointer',
      }}>{children}</select>
    </div>
  );

  return (
    <div ref={ref} style={{
      zIndex:200, overflowY:'auto',
      background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:8,
      boxShadow:'0 10px 30px rgba(0,0,0,0.18)', padding:14,
      ...(pos || HIDDEN_ANCHORED_STYLE),
    }}>
      <Row label="Organization" value={values.filterCompany} onChange={setters.setFilterCompany}>
        <option value="">All organizations</option>
        {options.companies.map(c => <option key={c} value={c}>{c}</option>)}
      </Row>
      <Row label="Country" value={values.filterCountry} onChange={setters.setFilterCountry}>
        <option value="">All countries</option>
        {options.countries.map(c => <option key={c} value={c}>{c}</option>)}
      </Row>
      <Row label="Role" value={values.filterFunction} onChange={setters.setFilterFunction}>
        <option value="">All roles</option>
        {options.functions.map(f => <option key={f} value={f}>{f}</option>)}
      </Row>
      <Row label="Project" value={values.filterProject} onChange={setters.setFilterProject}>
        <option value="">All projects</option>
        {sortedProjects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
      </Row>
      <div style={{display:'flex', justifyContent:'flex-end', marginTop:4}}>
        <button onClick={onClose} style={{...btnStyle, padding:'5px 12px'}}>Done</button>
      </div>
    </div>
  );
};

/* ── Embedded staffing grid (used by the chat fence) ──────────────
 * A self-contained, fully-interactive staffing widget: capacity
 * chart on top + expandable per-person table + cell-click edit
 * dialog, scoped by the filters the chat passed (search / project /
 * weeks). Reuses CapacityChart, PersonRow and EditStaffingDialog so
 * behaviour matches the Staffing page exactly. Edits are enforced
 * server-side by the upsert endpoint (governance); a 403 surfaces as
 * an inline error. */
export const StaffingEmbed = ({ search = '', projectId = '', weeks: weeksN = 12, userIds = null, selectedWeek = null, onSelectWeek = null, bare = false }) => {
  const isMobile = useIsMobile();
  // Authoritative scope: the chat resolved exactly these people, so the
  // table + chart show only them. Falls back to fuzzy search when the
  // chat didn't pass ids (e.g. a team-wide question).
  const scopeIds = React.useMemo(
    () => (Array.isArray(userIds) && userIds.length ? new Set(userIds) : null),
    [userIds],
  );
  const window = React.useMemo(() => {
    const n = Math.max(4, Math.min(26, Number(weeksN) || 12));
    return buildWindow(new Date(), 1, n - 2);
  }, [weeksN]);
  const today = React.useMemo(() => isoWeekOf(new Date()).isoWeek, []);
  const fromWeek = window[0].isoWeek, toWeek = window[window.length - 1].isoWeek;

  const [capacity, setCapacity] = React.useState(null);
  const [entries,  setEntries]  = React.useState([]);
  const [people,   setPeople]   = React.useState([]);
  const [projects, setProjects] = React.useState([]);
  const [clients,  setClients]  = React.useState([]);
  const [categories, setCategories] = React.useState(FALLBACK_CATEGORIES);
  const [editing,  setEditing]  = React.useState(null);
  const [expanded, setExpanded] = React.useState(() => new Set());
  const [error,    setError]    = React.useState(null);

  const chartScrollRef = React.useRef(null);
  const tableScrollRef = React.useRef(null);
  const syncingRef     = React.useRef(false);
  const mirror = (s, d) => () => {
    if (syncingRef.current) return;
    const src = s.current, dst = d.current; if (!src || !dst) return;
    syncingRef.current = true; dst.scrollLeft = src.scrollLeft;
    requestAnimationFrame(() => { syncingRef.current = false; });
  };

  const reload = React.useCallback(() => {
    setError(null);
    Promise.all([
      fetchStaffingCapacity({ from: fromWeek, to: toWeek, projectId: projectId || undefined, userIds: userIds || undefined }).catch(() => null),
      listStaffingEntries({ from: fromWeek, to: toWeek }).catch(() => []),
      listPeople().catch(() => []),
      listProjects().catch(() => []),
      listTaxonomies().then(t => t.clients || []).catch(() => []),
      listStaffingCategories().catch(() => FALLBACK_CATEGORIES),
    ]).then(([cap, ent, ppl, prj, cli, cats]) => {
      setCapacity(cap); setEntries(ent); setPeople(ppl); setProjects(prj); setClients(cli);
      setCategories(cats && cats.length ? cats : FALLBACK_CATEGORIES);
    }).catch(err => setError(err.message || String(err)));
  }, [fromWeek, toWeek, projectId, userIds]);
  React.useEffect(reload, [reload]);

  const grid = React.useMemo(() => {
    const m = new Map();
    for (const e of entries) {
      const wk = m.get(e.userId) || new Map();
      const arr = wk.get(e.isoWeek) || []; arr.push(e); wk.set(e.isoWeek, arr); m.set(e.userId, wk);
    }
    return m;
  }, [entries]);

  const projectUserIds = React.useMemo(() => {
    if (!projectId) return null;
    const ids = new Set();
    for (const e of entries) if (e.projectId === projectId && e.percentage > 0) ids.add(e.userId);
    return ids;
  }, [entries, projectId]);

  const filtered = React.useMemo(() => {
    const tokens = (search || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
    return people.filter(p => {
      if (scopeIds) return scopeIds.has(p.id);
      if (projectUserIds && !projectUserIds.has(p.id)) return false;
      if (!tokens.length) return true;
      const hay = [p.name, p.title, p.company, p.country, p.functionName].filter(Boolean).join(' ').toLowerCase();
      return tokens.every(t => hay.includes(t));
    });
  }, [people, search, projectUserIds, scopeIds]);

  const toggle = id => setExpanded(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const fillCells = async ({ userId, projectType, projectId, clientId, percentage, note, isoWeeks }) => {
    setError(null);
    try {
      for (const isoWeek of isoWeeks) {
        await upsertStaffingEntry({
          userId, isoWeek, projectType,
          projectId: projectType === 'client-project' ? projectId : null,
          clientId: clientId || null,
          percentage: Number(percentage), note: note || '',
        });
      }
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      reload();
    }
  };

  return (
    <div style={bare ? { background:'transparent' } : {margin:'10px 0', background:'var(--bg)'}}>
      {error && <div style={{...errStyle, padding:'6px 12px'}}>{error}</div>}
      <CapacityChart
        weeks={window} capacity={capacity} categories={categories} today={today} isMobile={isMobile}
        scrollRef={chartScrollRef} onScroll={mirror(chartScrollRef, tableScrollRef)}
      />
      <div ref={tableScrollRef} onScroll={mirror(tableScrollRef, chartScrollRef)}
           style={{overflowX:'auto', borderTop:'1px solid var(--border)'}}>
        <div style={gridHeader(window.length, isMobile)}>
          <div style={{...STICKY_COL_HEAD, padding: isMobile ? '6px 10px' : '8px 12px'}}>Person</div>
          {window.map(w => {
            const sel = onSelectWeek && w.isoWeek === selectedWeek;
            return (
              <div key={w.isoWeek}
                   onClick={onSelectWeek ? () => onSelectWeek(w.isoWeek) : undefined}
                   title={onSelectWeek ? `Open ${w.isoWeek} for booking` : undefined}
                   style={{
                     ...headerCell(w.isoWeek === today, isMobile),
                     cursor: onSelectWeek ? 'pointer' : undefined,
                     ...(sel ? { background:'var(--orange)', color:'#fff', borderBottom:'3px solid var(--orange)' } : {}),
                   }}>
                CW{w.week}
              </div>
            );
          })}
        </div>
        {filtered.length === 0 && <div style={{padding:16, textAlign:'center', color:'var(--fg-muted)', fontSize:12}}>No matching people.</div>}
        {filtered.map(p => (
          <PersonRow key={p.id} person={p} weeks={window} isMobile={isMobile}
            entriesByWeek={grid.get(p.id) || new Map()} today={today} projects={projects} clients={clients} categories={categories}
            expanded={projectId ? true : expanded.has(p.id)} onlyProjectId={projectId || null}
            onToggle={() => toggle(p.id)} onEdit={(uid, wk, ex) => setEditing({ userId: uid, isoWeek: wk, existing: ex || null })}
            onFill={fillCells}/>
        ))}
      </div>
      {editing && (
        <EditStaffingDialog {...editing} projects={projects} clients={clients} categories={categories}
          onClose={saved => { setEditing(null); if (saved) reload(); }}/>
      )}
    </div>
  );
};

/* ── Capacity chart ───────────────────────────────────────────── */

const CapacityChart = ({ weeks, capacity, categories = [], today, isMobile = false, scrollRef, onScroll }) => {
  const byWeek = React.useMemo(() => {
    const m = new Map();
    for (const w of (capacity?.weeks || [])) m.set(w.isoWeek, w);
    return m;
  }, [capacity]);

  // Segments rendered bottom-up: Billable (projects) → each category in
  // its admin-defined order/color → Free. Data-driven so a new category
  // shows up here automatically.
  const segments = React.useMemo(() => [
    { key: '__billable', label: 'Billable', color: 'var(--orange)', hoursOf: w => w.billableHours || 0 },
    ...(categories || []).map(c => ({
      key: c.slug, label: c.label, color: c.color || '#A0A0A0',
      hoursOf: w => (w.categoryHours && w.categoryHours[c.slug]) || 0,
    })),
    { key: '__free', label: 'Free', color: 'rgba(255,100,40,0.15)', hoursOf: w => w.freeHours || 0 },
  ], [categories]);

  const weekTotal = (w) => w ? segments.reduce((s, seg) => s + seg.hoursOf(w), 0) : 0;

  const max = React.useMemo(() => {
    let m = capacity?.availableHoursPerWeek || 100;
    for (const w of (capacity?.weeks || [])) {
      const total = weekTotal(w);
      if (total > m) m = total;
    }
    return m || 100;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capacity, segments]);

  return (
    <div style={{
      marginTop: isMobile ? 12 : 18, padding:'12px 0 0',
      background:'transparent',
    }}>
      {/* Header + legend stay fixed; only the bar grid below scrolls
          (kept in sync with the table). */}
      <div style={{
        padding: isMobile ? '0 12px 8px' : '0 16px 8px',
        fontSize:11.5, color:'var(--fg-muted)',
        display:'flex', flexDirection: isMobile ? 'column' : 'row',
        justifyContent:'space-between', alignItems: isMobile ? 'flex-start' : 'center',
        gap: isMobile ? 6 : 0,
      }}>
        <span>Available (client-facing roles): {Math.round(capacity?.availableHoursPerWeek || 0)} h/wk</span>
        <Legend segments={segments}/>
      </div>
      <div ref={scrollRef} onScroll={onScroll} style={{overflowX:'auto'}}>
      <div style={{...gridHeader(weeks.length, isMobile), background:'transparent', borderBottom:'none', alignItems:'end', minHeight: isMobile ? 110 : 140, paddingBottom:8}}>
        <div style={{...STICKY_COL_HEAD, padding: isMobile ? '0 8px' : '0 12px', fontSize:11.5, color:'var(--fg-muted)'}}>Capacity (hours)</div>
        {weeks.map(w => {
          const c = byWeek.get(w.isoWeek);
          const sum = weekTotal(c);
          const h = v => `${Math.max(0, (v / max) * (isMobile ? 80 : 110))}px`;
          return (
            <div key={w.isoWeek} style={{padding:'0 4px', display:'flex', flexDirection:'column', alignItems:'center'}}>
              <div style={{
                width:'100%', display:'flex', flexDirection:'column-reverse',
                border: w.isoWeek === today ? '1px solid var(--orange)' : '1px solid transparent', borderRadius:4,
              }}>
                {c && segments.map(seg => {
                  const hrs = seg.hoursOf(c);
                  return <div key={seg.key} title={`${seg.label} ${Math.round(hrs)}h`} style={{height:h(hrs), background:seg.color}}/>;
                })}
              </div>
              <div style={{fontSize:10, color: w.isoWeek === today ? 'var(--orange)' : 'var(--fg-muted)', marginTop:2, fontWeight: w.isoWeek === today ? 700 : 400}}>
                {Math.round(sum)}h
              </div>
            </div>
          );
        })}
      </div>
      </div>
    </div>
  );
};

const Legend = ({ segments = [] }) => (
  <div style={{display:'flex', gap:10, flexWrap:'wrap', fontSize:10.5}}>
    {segments.map(seg => (
      <span key={seg.key} style={{display:'inline-flex', alignItems:'center', gap:4}}>
        <i style={{display:'inline-block', width:8, height:8, background:seg.color, borderRadius:2, border:'1px solid var(--border)'}}/>{seg.label}
      </span>
    ))}
  </div>
);

/* Small padlock ("Schloss") marking booked actuals as read-only. */
const LockIcon = ({ size = 9, color = 'var(--fg-muted)' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{flexShrink:0, display:'inline-block', verticalAlign:'middle'}}>
    <rect x="5" y="11" width="14" height="9" rx="2" fill={color}/>
    <path d="M8 11V8a4 4 0 0 1 8 0v3" stroke={color} strokeWidth="2.4" fill="none"/>
  </svg>
);

/* Plan vs. Actuals+Plan segmented toggle. */
const ViewModeToggle = ({ value, onChange }) => {
  const opt = (id, label) => (
    <button
      onClick={() => onChange(id)}
      style={{
        padding:'5px 9px', fontSize:11.5, fontWeight:600, cursor:'pointer', border:'none',
        background: value === id ? 'var(--orange)' : 'transparent',
        color: value === id ? '#fff' : 'var(--fg-muted)',
      }}
    >{label}</button>
  );
  return (
    <div style={{display:'inline-flex', border:'1px solid var(--border)', borderRadius:6, overflow:'hidden', background:'var(--bg-card)'}}>
      {opt('mixed', 'Actuals + Plan')}
      {opt('plan', 'Plan')}
    </div>
  );
};

/* ── Per-person row + expansion ──────────────────────────────── */

const PersonRow = ({ person, weeks, entriesByWeek, today, projects, clients = [], categories = [], expanded, onToggle, onEdit, onFill, isMobile = false, onlyProjectId = null, viewMode = 'plan', bookedByWeek = null }) => {
  // Drag-to-fill: grab a filled project cell and paint its value across
  // the weeks you drag over (in either direction). `drag` holds the
  // source value plus the current [start,end] week-index range; the
  // commit happens on the global mouseup. dragRef mirrors it so the
  // listener always reads the live range without re-binding per move.
  const [drag, setDrag] = React.useState(null);
  const dragRef = React.useRef(null);
  dragRef.current = drag;

  React.useEffect(() => {
    if (!drag) return;
    const onUp = () => {
      const d = dragRef.current;
      setDrag(null);
      if (!d) return;
      const lo = Math.min(d.startIdx, d.endIdx);
      const hi = Math.max(d.startIdx, d.endIdx);
      if (lo === hi) return;            // no drag — let the click open the edit dialog
      const isoWeeks = [];
      for (let i = lo; i <= hi; i++) isoWeeks.push(weeks[i].isoWeek);
      onFill?.({
        userId: person.id,
        projectType: d.projectType,
        projectId: d.projectId,
        clientId: d.clientId || null,
        percentage: d.value,
        note: d.note,
        isoWeeks,
      });
    };
    window.addEventListener('mouseup', onUp);
    return () => window.removeEventListener('mouseup', onUp);
  }, [drag, weeks, person.id, onFill]);

  // Aggregate per week.
  const totals = new Map();
  // Per-project rows that appear in the visible window.
  const visibleProjectKeys = new Set();
  for (const [iso, arr] of entriesByWeek) {
    let total = 0;
    for (const e of arr) {
      total += e.percentage;
      if (e.percentage > 0) {
        const key = e.clientId ? `s:${e.projectType}:${e.clientId}`
          : e.projectType === 'client-project' ? `c:${e.projectId}` : `t:${e.projectType}`;
        visibleProjectKeys.add(key);
      }
    }
    totals.set(iso, total);
  }
  const buildProject = (key) => {
    if (key.startsWith('c:')) {
      const id = key.slice(2);
      const p = projects.find(x => x.id === id);
      const client = p ? clients.find(c => (c.id || c._id) === p.clientId) : null;
      return { key, label: p?.name || 'Project', projectType: 'client-project', projectId: id, client: client || null };
    }
    if (key.startsWith('s:')) {
      // 's:<itemType>:<clientId>' — a client-linked booked item (e.g. Sales).
      const parts = key.split(':');
      const type = parts[1] || 'sales';
      const clientId = parts[2];
      const client = clients.find(c => (c.id || c._id) === clientId) || null;
      const catLabel = categories.find(c => c.slug === type)?.label || (type[0].toUpperCase() + type.slice(1));
      return { key, label: client ? `${catLabel} · ${client.name}` : catLabel, projectType: type, projectId: null, clientId, client };
    }
    const type = key.slice(2);
    const meta = categories.find(c => c.slug === type);
    return { key, label: meta?.label || type, projectType: type, projectId: null, client: null };
  };

  // Plan keys drive the editable rows. In mixed mode we additionally
  // surface booked-only items (e.g. Sales) that have no plan counterpart.
  const planKeys = new Set(visibleProjectKeys);
  if (viewMode === 'mixed' && bookedByWeek) {
    for (const w of weeks) {
      const b = bookedByWeek.get(w.isoWeek);
      if (b) for (const key of b.items.keys()) visibleProjectKeys.add(key);
    }
  }
  let visibleProjects = [...visibleProjectKeys].map(key => {
    const proj = buildProject(key);
    proj.bookedOnly = !planKeys.has(key);
    return proj;
  });
  // Project filter: collapse the breakdown to just that one line.
  if (onlyProjectId) visibleProjects = visibleProjects.filter(p => p.projectId === onlyProjectId);

  return (
    <>
      <div style={gridRow(weeks.length, isMobile)}>
        <div style={{...STICKY_COL, padding: isMobile ? '8px 8px' : '8px 12px', display:'flex', alignItems:'center', gap:6, cursor:'pointer', userSelect:'none'}} onClick={onToggle}>
          <span style={{fontSize:11, color:'var(--fg-muted)', display:'inline-block', width:10, transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)', transition:'transform 80ms'}}>▶</span>
          <div style={{minWidth:0, flex:1}}>
            <div style={{whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>
              <EntityLink type="person" handle={person.id} muted title={`Open ${person.name}`} style={{cursor:'pointer'}}>
                <span onClick={e => e.stopPropagation()}>{person.name}</span>
              </EntityLink>
            </div>
            <div style={{fontSize:10.5, color:'var(--fg-muted)'}}>{person.title || ''}</div>
          </div>
        </div>
        {weeks.map(w => {
          const isToday = w.isoWeek === today;
          const booked = bookedByWeek ? bookedByWeek.get(w.isoWeek) : null;
          // Booked weeks are locked actuals — show the rolled-up % + lock.
          if (booked) {
            const bpct = booked.totalPercent || 0;
            const bunder = bpct > 0 && bpct < 80;
            return (
              <div key={w.isoWeek}
                   title={`Booked actuals — ${booked.totalHours ?? '?'}h this week (locked)`}
                   style={{...cellStyle(isToday, isMobile), cursor:'default', display:'flex', alignItems:'center', justifyContent:'center', gap:3}}>
                <LockIcon/><span style={{fontWeight:600}}>{fmtPct(bpct)}%</span>
                {bunder && <span title={`Below 80% — only ${fmtPct(bpct)}%`} style={warnStyle}>!</span>}
              </div>
            );
          }
          const pct = totals.get(w.isoWeek) || 0;
          const under = pct > 0 && pct < 80;
          return (
            <div key={w.isoWeek}
                 onClick={() => onEdit(person.id, w.isoWeek, null)}
                 style={{...cellStyle(isToday, isMobile), cursor:'pointer'}}>
              <span style={{fontWeight: pct ? 600 : 400}}>{pct ? `${fmtPct(pct)}%` : '—'}</span>
              {under && <span title={`Below 80% target — only ${fmtPct(pct)}%`} style={warnStyle}>!</span>}
            </div>
          );
        })}
      </div>

      {expanded && (
        <>
          {visibleProjects.map(proj => {
            const match = list => list.find(x => {
              if (proj.projectType === 'client-project') return x.projectType === 'client-project' && x.projectId === proj.projectId;
              if (proj.clientId) return x.projectType === proj.projectType && (x.clientId || null) === proj.clientId;
              return x.projectType === proj.projectType && !x.projectId && !x.clientId;
            });
            const dragActive = drag && drag.projKey === proj.key;
            const lo = dragActive ? Math.min(drag.startIdx, drag.endIdx) : -1;
            const hi = dragActive ? Math.max(drag.startIdx, drag.endIdx) : -1;
            return (
            <div key={proj.key} style={{...gridRow(weeks.length, isMobile), background:'transparent', fontSize:11.5}}>
              <div style={{...STICKY_COL, padding: isMobile ? '6px 6px 6px 26px' : '6px 12px 6px 38px', color:'var(--fg-muted)', minWidth:0, display:'flex', alignItems:'center', gap:7}}>
                {proj.client && proj.client.logoUrl && (
                  <img src={proj.client.logoUrl} alt="" style={{width:18, height:18, objectFit:'contain', borderRadius:3, flexShrink:0, background:'var(--bg-card)'}}/>
                )}
                <div style={{minWidth:0}}>
                  {proj.client && (
                    <div style={{fontSize:9.5, fontWeight:400, color:'var(--fg-muted)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', lineHeight:1.25}}>
                      <EntityLink type="client" handle={entityHandle(proj.client)} muted title={`Open ${proj.client.name}`}>{proj.client.name}</EntityLink>
                    </div>
                  )}
                  <div style={{fontSize:12, fontWeight:600, color:'var(--fg)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', lineHeight:1.3}}>
                    {proj.projectId
                      ? <EntityLink type="project" handle={proj.projectId} muted title={`Open ${proj.label}`}>{proj.label}</EntityLink>
                      : proj.label}
                  </div>
                </div>
              </div>
              {weeks.map((w, wi) => {
                const isToday = w.isoWeek === today;
                // Booked actual for this item+week → locked cell + lock icon.
                const bookedItem = bookedByWeek ? bookedByWeek.get(w.isoWeek)?.items.get(proj.key) : null;
                if (bookedItem) {
                  return (
                    <div key={w.isoWeek}
                         title={`Booked actual — ${bookedItem.hours ?? '?'}h (locked)`}
                         style={{...cellStyle(isToday, isMobile), cursor:'default', fontWeight:600, display:'flex', alignItems:'center', justifyContent:'center', gap:3}}>
                      <LockIcon/>{fmtPct(bookedItem.percent)}%
                    </div>
                  );
                }
                // Booked-only client-linked items (Sales · Client with an
                // actual but no plan row) are read-only in the plan grid.
                // A *planned* client-linked row stays fully editable.
                if (proj.key.startsWith('s:') && proj.bookedOnly) {
                  return <div key={w.isoWeek} style={{...cellStyle(isToday, isMobile), color:'var(--fg-muted)'}}>·</div>;
                }
                const e = match(entriesByWeek.get(w.isoWeek) || []);
                const pct = e?.percentage || 0;
                // Empty cell: prefill the edit dialog from the nearest
                // filled cell to its left in this project row, so filling
                // the "next" week carries the previous value forward.
                let initial = e;
                if (!e) {
                  let prev = null;
                  for (let i = wi - 1; i >= 0; i--) {
                    const pe = match(entriesByWeek.get(weeks[i].isoWeek) || []);
                    if (pe && pe.percentage > 0) { prev = pe; break; }
                  }
                  initial = {
                    projectType: proj.projectType,
                    projectId: proj.projectId,
                    clientId: proj.clientId || null,
                    percentage: prev ? prev.percentage : 0,
                    note: prev ? (prev.note || '') : '',
                  };
                }
                const base = cellStyle(w.isoWeek === today, isMobile);
                const painting = dragActive && wi >= lo && wi <= hi;
                return (
                  <div key={w.isoWeek}
                       onMouseDown={pct > 0 ? ev => {
                         ev.preventDefault();
                         setDrag({ projKey: proj.key, projectType: proj.projectType, projectId: proj.projectId, clientId: proj.clientId || null, value: pct, note: e.note || '', startIdx: wi, endIdx: wi });
                       } : undefined}
                       onMouseEnter={() => { if (dragRef.current && dragRef.current.projKey === proj.key) setDrag(d => d ? { ...d, endIdx: wi } : d); }}
                       onClick={() => onEdit(person.id, w.isoWeek, initial)}
                       title={pct > 0 ? 'Drag to copy this value across weeks' : undefined}
                       style={{
                         ...base, fontWeight: pct ? 600 : 400, userSelect: 'none',
                         cursor: pct > 0 ? 'grab' : 'pointer',
                         background: painting ? 'rgba(255,100,40,0.18)' : base.background,
                         boxShadow: painting ? 'inset 0 0 0 1px var(--orange)' : undefined,
                       }}>
                    {pct ? `${fmtPct(pct)}%` : '·'}
                  </div>
                );
              })}
            </div>
            );
          })}
          <div style={{...gridRow(weeks.length, isMobile), background:'transparent'}}>
            <div style={{...STICKY_COL, padding: isMobile ? '6px 6px 8px 26px' : '6px 12px 8px 38px', fontSize:11.5}}>
              <button onClick={() => onEdit(person.id, today, { projectType: 'client-project', projectId: null, percentage: 50 })}
                      style={addBtn}>+ Add project</button>
            </div>
            {weeks.map(w => <div key={w.isoWeek} style={cellStyle(w.isoWeek === today, isMobile)}/>)}
          </div>
        </>
      )}
    </>
  );
};

/* ── Cell edit dialog ────────────────────────────────────────── */

const EditStaffingDialog = ({ userId, isoWeek, existing, projects, clients = [], categories = [], onClose }) => {
  const [projectType, setProjectType] = React.useState(existing?.projectType || 'client-project');
  const [projectId,   setProjectId]   = React.useState(existing?.projectId || (projects[0]?.id || ''));
  const [clientId,    setClientId]    = React.useState(existing?.clientId || '');
  const [percentage,  setPercentage]  = React.useState(existing?.percentage ?? 50);
  const [note,        setNote]        = React.useState(existing?.note || '');
  const [saving,      setSaving]      = React.useState(false);
  const [conflict,    setConflict]    = React.useState(null);
  const [error,       setError]       = React.useState(null);

  const isClient = projectType === 'client-project';
  const isClientLinked = !!categories.find(c => c.slug === projectType)?.clientLinked;

  const save = async (force = false) => {
    setSaving(true); setError(null); setConflict(null);
    try {
      await upsertStaffingEntry({
        userId, isoWeek, projectType,
        projectId: isClient ? projectId : null,
        clientId: isClientLinked ? clientId : null,
        percentage: Number(percentage),
        note,
        expectedUpdatedAt: force ? undefined : existing?.updatedAt,
      });
      onClose(true);
    } catch (err) {
      if (err.code === 'conflict') {
        setConflict('Someone else changed this entry while you were editing. Discard your edit, or save anyway to overwrite.');
      } else {
        setError(err.message || String(err));
      }
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!existing?.id) return;
    if (!confirm('Remove this staffing entry?')) return;
    try {
      await deleteStaffingEntry(existing.id);
      onClose(true);
    } catch (err) { setError(err.message || String(err)); }
  };

  return (
    <div className="modal-mask" data-modal-open onClick={() => onClose(false)}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{position:'relative', maxWidth:440}}>
        <button className="modal__close" onClick={() => onClose(false)}>×</button>
        <h2 style={{fontSize:18, fontWeight:600, margin:'0 0 4px'}}>Staffing — {isoWeek}</h2>
        <p style={{fontSize:11.5, color:'var(--fg-muted)', margin:'0 0 14px'}}>Person ID: {userId}</p>

        <Field label="Type">
          <select value={projectType} onChange={e => setProjectType(e.target.value)} style={inputStyle}>
            {typeOptions(categories).map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </Field>

        {isClient && (
          <Field label="Project">
            <select value={projectId} onChange={e => setProjectId(e.target.value)} style={inputStyle}>
              {projects.length === 0 && <option value="">No projects available</option>}
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </Field>
        )}

        {isClientLinked && (
          <Field label="Client">
            <select value={clientId} onChange={e => setClientId(e.target.value)} style={inputStyle}>
              <option value="">Select a client…</option>
              {clients.map(c => <option key={c.id || c._id} value={c.id || c._id}>{c.name}</option>)}
            </select>
          </Field>
        )}

        <Field label={`Percentage (${percentage}%)`}>
          <input type="range" min={0} max={100} step={5} value={percentage}
                 onChange={e => setPercentage(Number(e.target.value))}
                 style={{width:'100%'}}/>
        </Field>

        <Field label="Note">
          <input value={note} onChange={e => setNote(e.target.value)} placeholder="optional" style={inputStyle}/>
        </Field>

        {error && <div style={errStyle}>{error}</div>}
        {conflict && (
          <div style={{marginTop:12, padding:12, border:'1px solid #B83C0F', background:'rgba(184,60,15,0.07)', borderRadius:6, fontSize:12.5, color:'#B83C0F'}}>
            {conflict}
            <div style={{marginTop:8, display:'flex', gap:8}}>
              <button onClick={() => save(true)} style={primaryBtn}>Save anyway</button>
              <button onClick={() => onClose(true)} style={ghostBtn}>Discard</button>
            </div>
          </div>
        )}

        <div style={{marginTop:18, display:'flex', gap:8}}>
          {!conflict && <button onClick={() => save(false)} disabled={saving || (isClient && !projectId) || (isClientLinked && !clientId)} style={primaryBtn}>{saving ? 'Saving…' : 'Save'}</button>}
          {!conflict && <button onClick={() => { setPercentage(0); setNote(''); }} title="Reset percentage and note to start over" style={ghostBtn}>Clear</button>}
          {existing?.id && <button onClick={remove} style={{...ghostBtn, color:'#A20235'}}>Delete</button>}
          <button onClick={() => onClose(false)} style={ghostBtn}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div style={{marginBottom:12}}>
    <label style={{fontSize:11.5, fontWeight:600, color:'var(--fg-muted)'}}>{label}</label>
    {children}
  </div>
);

/* ── style helpers ───────────────────────────────────────────── */

// Freeze the first column (person / project name) while the calendar-week
// columns scroll horizontally. The cell sticks to the left of the scroll
// container and needs an opaque background to mask the weeks sliding under it.
const STICKY_COL = { position: 'sticky', left: 0, zIndex: 2, background: 'var(--bg)' };
const STICKY_COL_HEAD = { position: 'sticky', left: 0, zIndex: 3, background: 'var(--bg)' };

const gridHeader = (n, isMobile = false) => ({
  display:'grid',
  gridTemplateColumns:`${isMobile ? '120px' : '220px'} repeat(${n}, minmax(${isMobile ? 48 : 56}px, 1fr))`,
  minWidth:`${(isMobile ? 120 : 220) + n * (isMobile ? 48 : 56)}px`,
  background:'transparent', fontWeight:600, fontSize:11.5,
  borderBottom:'1px solid var(--border)',
});
const gridRow = (n, isMobile = false) => ({
  display:'grid',
  gridTemplateColumns:`${isMobile ? '120px' : '220px'} repeat(${n}, minmax(${isMobile ? 48 : 56}px, 1fr))`,
  minWidth:`${(isMobile ? 120 : 220) + n * (isMobile ? 48 : 56)}px`,
  borderTop:'1px solid var(--border)', fontSize:12,
  alignItems:'stretch',
});
const headerCell = (isToday, isMobile = false) => ({
  padding: isMobile ? '6px 4px' : '8px 6px', textAlign:'center',
  background: isToday ? 'var(--orange)' : 'transparent',
  color:      isToday ? '#fff' : 'inherit',
  fontSize: isMobile ? 10.5 : 11.5,
});
const cellStyle = (isToday, isMobile = false) => ({
  padding: isMobile ? '10px 2px' : '8px 4px',
  textAlign:'center', position:'relative',
  background: isToday ? 'rgba(255,100,40,0.08)' : 'transparent',
  fontSize: isMobile ? 11.5 : 12,
  minHeight: 36,
});
const warnStyle = { position:'absolute', top:4, right:6, color:'var(--orange)', fontWeight:700, fontSize:11 };
const btnStyle  = { padding:'6px 10px', borderRadius:6, fontSize:12, border:'1px solid var(--border)', background:'var(--bg-card)', cursor:'pointer' };
const filterSelectStyle = (isMobile) => ({
  flex: isMobile ? 'none' : '0 1 auto',
  padding:'8px 10px', border:'1px solid var(--border)', borderRadius:6,
  fontSize:13, background:'var(--bg-card)', cursor:'pointer',
  maxWidth: isMobile ? '100%' : 180,
});
const addBtn    = { background:'transparent', border:'none', cursor:'pointer', color:'var(--orange)', fontSize:11.5, fontWeight:600, padding:0 };
const primaryBtn= { padding:'8px 14px', borderRadius:6, fontSize:13, fontWeight:600, background:'var(--orange)', color:'#fff', border:'none', cursor:'pointer' };
const ghostBtn  = { padding:'8px 14px', borderRadius:6, fontSize:12, background:'transparent', border:'1px solid var(--border)', cursor:'pointer' };
const inputStyle= { display:'block', width:'100%', marginTop:6, padding:'8px 10px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, boxSizing:'border-box' };
const errStyle  = { color:'#A20235', fontSize:12, margin:'8px 0' };
